import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Conversations, Users, type Conversation, type ConversationType, type User } from '@/lib/api';
import UserAvatar from './UserAvatar';

interface Props {
  type: ConversationType;
  onClose: () => void;
  onCreated: (c: Conversation) => void;
}

export default function NewChatModal({ type, onClose, onCreated }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [selected, setSelected] = useState<Map<string, User>>(new Map());
  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      Users.search(query.trim())
        .then((res) => {
          if (cancelled) return;
          setResults(res.users ?? []);
        })
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setLoading(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const isGroup = type === 'group';
  const canSubmit = useMemo(() => {
    if (isGroup) return selected.size >= 1 && groupName.trim().length >= 1;
    return selected.size === 1;
  }, [isGroup, selected, groupName]);

  function toggle(u: User) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(u.id)) next.delete(u.id);
      else if (!isGroup) {
        next.clear();
        next.set(u.id, u);
      } else {
        next.set(u.id, u);
      }
      return next;
    });
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      const userIds = Array.from(selected.keys());
      const { conversation } = await Conversations.create({
        type,
        name: isGroup ? groupName.trim() : undefined,
        userIds,
      });
      onCreated(conversation);
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to create';
      setErr(m);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isGroup ? 'New group' : 'New chat'}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-md p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isGroup ? 'New group' : 'New chat'}</h2>
          <button type="button" aria-label="Close" className="btn-ghost px-2" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isGroup && (
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Group name</span>
            <input
              className="input"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={64}
              placeholder="My group"
            />
          </label>
        )}

        <label className="mb-2 block">
          <span className="mb-1 block text-sm font-medium">{isGroup ? 'Add members' : 'Find a user'}</span>
          <input
            ref={inputRef}
            type="search"
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isGroup ? 'Search by username…' : 'Type a username…'}
          />
        </label>

        {selected.size > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {Array.from(selected.values()).map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u)}
                className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-1 text-xs text-brand-800 hover:bg-brand-200 dark:bg-brand-700/40 dark:text-brand-100 dark:hover:bg-brand-700/60"
                aria-label={`Remove ${u.displayName || u.username}`}
              >
                {u.displayName || u.username}
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}

        <div className="nice-scroll max-h-64 overflow-y-auto rounded-lg border border-ink-100 dark:border-ink-700">
          {loading && <div className="px-3 py-2 text-xs text-ink-500 dark:text-ink-300">Searching…</div>}
          {!loading && query.trim() && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-ink-500 dark:text-ink-300">No users found.</div>
          )}
          <ul className="divide-y divide-ink-100 dark:divide-ink-700">
            {results.map((u) => {
              const checked = selected.has(u.id);
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => toggle(u)}
                    className={clsx(
                      'flex w-full items-center gap-3 px-3 py-2 text-left transition',
                      checked ? 'bg-brand-50 dark:bg-ink-700' : 'hover:bg-ink-100 dark:hover:bg-ink-700/70',
                    )}
                  >
                    <UserAvatar name={u.displayName || u.username} src={u.avatarUrl} size={36} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{u.displayName || u.username}</span>
                      <span className="block truncate text-xs text-ink-500 dark:text-ink-300">@{u.username}</span>
                    </span>
                    {checked && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {err && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/60 dark:text-red-300">{err}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canSubmit || submitting}
            onClick={submit}
          >
            {submitting ? 'Creating…' : isGroup ? 'Create group' : 'Start chat'}
          </button>
        </div>
      </div>
    </div>
  );
}
